import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Loader2, Mail, Paperclip, Star, X, FolderInput, Tag, ExternalLink, Reply, CheckSquare, Check, Square, Search, Trash2, Folder, MoreHorizontal, Undo2, Bot, Plus, LayoutGrid, LayoutList } from 'lucide-react';
import { mailApi, todoApi, boardsApi, aiApi, SmartAiColumn, preferencesApi } from '../../api/client';
import { smartColumnsApi, UpdateSmartColumnInput } from '../../features/ai/api/ai.api';
import KanbanColumn from './KanbanColumn';
import SmartKanbanColumn from './SmartKanbanColumn';
import AiKanbanColumn from './AiKanbanColumn';
import MeetingPrepColumn from './MeetingPrepColumn';
import SmartColumnModal, { SmartColumnData } from './SmartColumnModal';
import { SmartColumnCreator } from '../../features/ai/components/SmartColumnCreator';
// AI Column Creator moved to BoardEditor
import ReadingPane from '../mail/ReadingPane';
import MailModal from '../mail/MailModal';
import { AddToTodoModal } from './AddToTodoModal';
import { useUiStore, useIsMobile, BoardViewMode } from '../../stores/ui.store';
import { toast } from 'sonner';
import { cn } from '../../utils/cn';
import { formatDate } from '../../utils/date';

interface KanbanBoardProps {
  board: any;
  accounts: any[];
  showOnlyWithTasks?: boolean; // Punkt 1: Task-Filter
}

export default function KanbanBoard({ board, accounts, showOnlyWithTasks = false }: KanbanBoardProps) {
  const queryClient = useQueryClient();
  const [selectedMail, setSelectedMail] = useState<{
    accountId: string;
    messageId: string;
    provider: string;
  } | null>(null);
  
  const [activeMessage, setActiveMessage] = useState<any>(null);
  
  // Get reading pane mode, view mode and mobile status
  const readingPaneMode = useUiStore((state) => state.readingPaneMode);
  const boardViewMode = useUiStore((state) => state.boardViewMode);
  const setBoardViewMode = useUiStore((state) => state.setBoardViewMode);
  const isMobile = useIsMobile();
  
  // Determine if we should use popup (mobile always uses popup, desktop respects setting)
  const usePopup = isMobile || readingPaneMode === 'popup';
  
  // Resizable divider state (list-mode only)
  const [dividerPos, setDividerPos] = useState(50);
  const isDraggingDivider = useRef(false);
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingDivider.current) return;
      const container = document.getElementById('board-split-container');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setDividerPos(Math.min(70, Math.max(30, pct)));
    };
    const handleMouseUp = () => { isDraggingDivider.current = false; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  
  // Mobile quick actions state
  const [quickActionsMail, setQuickActionsMail] = useState<{
    message: any;
    accountId: string;
    columnId?: string;
  } | null>(null);
  
  // Add to Todo modal state
  const [addTodoMail, setAddTodoMail] = useState<{
    message: any;
    accountId: string;
    provider: string;
  } | null>(null);
  
  // Smart column edit state
  const [editSmartColumn, setEditSmartColumn] = useState<{
    id: string;
    displayName: string;
    sourceId: string;
    mailAccountId: string;
    filterConfig?: any;
    icon?: string;
  } | null>(null);
  
  // AI Column edit state
  const [editAiColumn, setEditAiColumn] = useState<SmartAiColumn | null>(null);
  
  // AI Column creator state
  // AI Column Creator moved to BoardEditor
  
  // Filter out hidden columns (Punkt 4)
  const allColumns = board.columns || [];
  const columns = allColumns.filter((col: any) => !col.hidden);
  
  // Collect hidden category names for filtering messages in folder columns
  // If a mail has one of these categories, it should be hidden from folders
  const hiddenCategoryNames = useMemo(() => {
    return allColumns
      .filter((col: any) => col.hidden && col.sourceType === 'category')
      .map((col: any) => col.sourceId.replace(/^category:/, ''));
  }, [allColumns]);
  
  // Track shown message IDs per column for duplicate filtering
  const [columnMessageIds, setColumnMessageIds] = useState<Record<string, string[]>>({});
  
  // Track summary mode per column (backend-persisted via user preferences)
  const [summaryModes, setSummaryModes] = useState<Record<string, boolean>>({});
  
  // Fetch column settings from backend preferences
  const { data: preferencesData } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => preferencesApi.get(),
    staleTime: 60 * 1000, // 1 minute cache
  });
  
  // Sync summaryModes from backend preferences
  useEffect(() => {
    if (preferencesData?.columnSettings) {
      const modes: Record<string, boolean> = {};
      for (const [columnId, settings] of Object.entries(preferencesData.columnSettings)) {
        if (settings.summaryMode !== undefined) {
          modes[columnId] = settings.summaryMode;
        }
      }
      setSummaryModes(modes);
    }
  }, [preferencesData]);
  
  // Mutation to update column summary mode
  const updateSummaryModeMutation = useMutation({
    mutationFn: ({ columnId, summaryMode }: { columnId: string; summaryMode: boolean }) =>
      preferencesApi.setColumnSummaryMode(columnId, summaryMode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });
  
  // Toggle summary mode for a column
  const toggleSummaryMode = useCallback((columnId: string) => {
    const newValue = !summaryModes[columnId];
    // Optimistic update
    setSummaryModes(prev => ({ ...prev, [columnId]: newValue }));
    // Persist to backend
    updateSummaryModeMutation.mutate({ columnId, summaryMode: newValue });
  }, [summaryModes, updateSummaryModeMutation]);
  
  // ==== AI COLUMNS SUPPORT ====
  
  // Get AI columns from board (loaded from backend with smartAiColumns relation)
  const aiColumns: SmartAiColumn[] = board.smartAiColumns || [];
  const enabledAiColumns = aiColumns.filter(c => c.enabled);
  
  // State for AI scanning
  const [isAiScanning, setIsAiScanning] = useState(false);
  
  // Get primary account for AI operations
  const primaryAccount = accounts[0];
  
  // Fetch AI matches for all columns in this board (with details: confidence + reasoning)
  const { data: aiMatchesData, refetch: refetchAiMatches } = useQuery({
    queryKey: ['aiMatches', board.id, primaryAccount?.id],
    queryFn: () => {
      if (!primaryAccount || enabledAiColumns.length === 0) {
        return { matches: {}, detailed: true as const };
      }
      return aiApi.getAiMatchesWithDetails(board.id, primaryAccount.id);
    },
    enabled: !!primaryAccount && enabledAiColumns.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - evaluations are cached in DB
    refetchInterval: 2 * 60 * 1000, // Auto-refresh every 2 minutes
  });
  
  // Full evaluation details per column
  const aiMatchesDetailed: Record<string, Array<{ messageId: string; confidence: number; reasoning: string | null }>> = 
    aiMatchesData?.matches || {};
  
  // Load folders for AI column source selection
  const { data: foldersForAiData } = useQuery({
    queryKey: ['folders', primaryAccount?.id],
    queryFn: () => mailApi.getFolders(primaryAccount!.id),
    enabled: !!primaryAccount,
    staleTime: 5 * 60 * 1000,
  });
  
  const foldersForAi = (foldersForAiData?.folders || []) as Array<{ id: string; name: string; displayName?: string; type?: string }>;
  
  // Legacy format for backward compatibility (just message IDs)
  const aiMatches: Record<string, string[]> = Object.fromEntries(
    Object.entries(aiMatchesDetailed).map(([colId, evals]) => [colId, evals.map(e => e.messageId)])
  );
  
  // Auto-scan: When board loads with AI columns, trigger scan for new messages
  useEffect(() => {
    const triggerAutoScan = async () => {
      if (!primaryAccount || enabledAiColumns.length === 0) return;
      
      // Get message IDs from all loaded columns
      const allMessageIds = Object.values(columnMessageIds).flat();
      if (allMessageIds.length === 0) return;
      
      // Only scan up to 100 messages at a time
      const messageIdsToScan = [...new Set(allMessageIds)].slice(0, 100);
      
      try {
        setIsAiScanning(true);
        const result = await aiApi.triggerAiScan(board.id, primaryAccount.id, messageIdsToScan);
        
        if (result.queued > 0) {
          // AI debug removed for production
          
          // Poll for completion - refetch matches after a delay
          setTimeout(() => {
            refetchAiMatches();
            setIsAiScanning(false);
          }, 3000); // Wait 3 seconds before first check
          
          // Additional poll after more time for larger batches
          if (result.queued > 10) {
            setTimeout(() => refetchAiMatches(), 10000);
          }
        } else {
          setIsAiScanning(false);
        }
      } catch (error) {
        console.error('[AI] Auto-scan failed:', error);
        setIsAiScanning(false);
      }
    };
    
    // Debounce: only trigger after messages have been loaded
    const timer = setTimeout(triggerAutoScan, 2000);
    return () => clearTimeout(timer);
  }, [board.id, primaryAccount?.id, enabledAiColumns.length, columnMessageIds]);
  
  // ==== END AI COLUMNS SUPPORT ====
  
  // ==== TODO SYNC (Microsoft/Google → Kolumi) ====
  // Sync todo status from external providers when board loads
  useEffect(() => {
    const syncTodos = async () => {
      try {
        // Sync all accounts in background (non-blocking)
        const result = await todoApi.syncAll();
        const totalUpdated = Object.values(result.results).reduce((sum, r) => sum + r.updated, 0);
        if (totalUpdated > 0) {
          // Refresh all todo link queries so UI updates
          queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
          console.log(`[TodoSync] Updated ${totalUpdated} tasks from external providers`);
        }
      } catch (error) {
        // Silent fail - sync is best-effort
        console.warn('[TodoSync] Background sync failed:', error);
      }
    };
    
    // Trigger sync shortly after board loads
    const timer = setTimeout(syncTodos, 1000);
    return () => clearTimeout(timer);
  }, [board.id, queryClient]);
  // ==== END TODO SYNC ====
  
  // Callback for columns to report their loaded messages
  const handleMessagesLoaded = useCallback((columnId: string, messageIds: string[]) => {
    setColumnMessageIds(prev => {
      if (JSON.stringify(prev[columnId]) === JSON.stringify(messageIds)) {
        return prev; // No change
      }
      return { ...prev, [columnId]: messageIds };
    });
  }, []);
  
  // Calculate excluded IDs for each column
  // Priority rules:
  // 1. Smart Columns: Priority based on sortOrder (first smart column wins)
  //    - Each smart column excludes IDs from ALL previous smart columns
  // 2. Categories > Folders (legacy behavior)
  //    - Categories show everything (no excludes)
  //    - Folders exclude messages that appear in any category column
  const getExcludedIds = useCallback((columnIndex: number): Set<string> => {
    if (!board.hideDuplicates) return new Set();
    
    const currentColumn = columns[columnIndex];
    const excluded = new Set<string>();
    
    // Smart columns: exclude IDs from ALL previous smart columns (priority order)
    if (currentColumn?.sourceType === 'smart') {
      // Find all smart columns and sort by sortOrder
      const smartColumns = columns
        .map((col: any, idx: number) => ({ ...col, originalIndex: idx }))
        .filter((col: any) => col.sourceType === 'smart')
        .sort((a: any, b: any) => a.sortOrder - b.sortOrder);
      
      // Find current column's position in smart columns
      const currentSmartIndex = smartColumns.findIndex((c: any) => c.id === currentColumn.id);
      
      // Exclude IDs from all PREVIOUS smart columns (lower priority = higher index in sorted list = shown first)
      for (let i = 0; i < currentSmartIndex; i++) {
        const prevSmartCol = smartColumns[i];
        if (columnMessageIds[prevSmartCol.id]) {
          columnMessageIds[prevSmartCol.id].forEach((id: string) => excluded.add(id));
        }
      }
      
      return excluded;
    }
    
    // Categories have priority - they don't exclude anything
    if (currentColumn?.sourceType === 'category') {
      return excluded;
    }
    
    // Folder columns (including inbox) exclude messages from ALL category columns
    // INCLUDING hidden ones (e.g. "Erledigt" column that's hidden but still categorizes mails)
    if (currentColumn?.sourceType === 'folder') {
      // Collect all IDs from category columns (use allColumns to include hidden!)
      for (let i = 0; i < allColumns.length; i++) {
        const col = allColumns[i];
        if (col.sourceType === 'category' && columnMessageIds[col.id]) {
          columnMessageIds[col.id].forEach((id: string) => excluded.add(id));
        }
      }
    }
    
    return excluded;
  }, [board.hideDuplicates, allColumns, columns, columnMessageIds]);
  
  // Configure drag sensors - both pointer (desktop) and touch (mobile)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activating
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // Long press delay for touch
        tolerance: 5, // Movement tolerance during delay
      },
    })
  );
  
  // Handle quick actions for mobile
  const handleQuickAction = useCallback((mailData: { message: any; accountId: string; columnId: string }) => {
    setQuickActionsMail(mailData);
  }, []);
  
  // Handle add to todo from card
  const handleAddTodo = useCallback((mailData: { message: any; accountId: string }) => {
    const account = accounts.find(a => a.id === mailData.accountId);
    if (account) {
      setAddTodoMail({
        message: mailData.message,
        accountId: mailData.accountId,
        provider: account.provider || 'microsoft',
      });
    }
  }, [accounts]);
  
  const handleMoveToColumn = useCallback((targetColumn: any) => {
    if (!quickActionsMail) return;
    
    if (targetColumn.sourceType === 'folder') {
      moveMutation.mutate({
        accountId: quickActionsMail.accountId,
        messageId: quickActionsMail.message.id,
        targetFolderId: targetColumn.sourceId,
      });
    } else if (targetColumn.sourceType === 'category') {
      const categoryName = targetColumn.sourceId.replace(/^category:/, '');
      categoryMutation.mutate({
        accountId: quickActionsMail.accountId,
        messageId: quickActionsMail.message.id,
        categoryName,
      });
    }
    
    setQuickActionsMail(null);
  }, [quickActionsMail]);
  
  // Get account by ID
  const getAccount = (accountId: string) => {
    return accounts.find(a => a.id === accountId);
  };
  
  // Move message mutation (for folders)
  const moveMutation = useMutation({
    mutationFn: async ({ 
      accountId, 
      messageId, 
      targetFolderId,
    }: { 
      accountId: string; 
      messageId: string; 
      targetFolderId: string;
    }) => {
      await mailApi.moveMessage(accountId, messageId, targetFolderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      toast.success('E-Mail verschoben');
    },
    onError: (error) => {
      toast.error('Fehler beim Verschieben');
      console.error('Move failed:', error);
    },
  });
  
  // Set category mutation (for categories)
  const categoryMutation = useMutation({
    mutationFn: async ({ 
      accountId, 
      messageId, 
      categoryName,
    }: { 
      accountId: string; 
      messageId: string; 
      categoryName: string;
    }) => {
      await mailApi.setCategory(accountId, messageId, categoryName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      toast.success('Kategorie gesetzt');
    },
    onError: (error) => {
      toast.error('Fehler beim Setzen der Kategorie');
      console.error('Category failed:', error);
    },
  });
  
  // Remove category mutation
  const removeCategoryMutation = useMutation({
    mutationFn: async ({ 
      accountId, 
      messageId, 
      categoryName,
    }: { 
      accountId: string; 
      messageId: string; 
      categoryName?: string; // undefined = remove all
    }) => {
      await mailApi.removeCategory(accountId, messageId, categoryName);
    },
    onSuccess: (_, { categoryName }) => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      toast.success(categoryName ? 'Kategorie entfernt' : 'Alle Kategorien entfernt');
    },
    onError: (error) => {
      toast.error('Fehler beim Entfernen der Kategorie');
      console.error('Remove category failed:', error);
    },
  });
  
  // Delete message mutation (move to trash with undo)
  const deleteMutation = useMutation({
    mutationFn: async ({ 
      accountId, 
      messageId,
    }: { 
      accountId: string; 
      messageId: string;
    }) => {
      return mailApi.deleteMessage(accountId, messageId);
    },
    onSuccess: (result, { accountId, messageId }) => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      
      // Show toast with undo option
      toast('E-Mail gelöscht', {
        icon: <Trash2 className="w-4 h-4" />,
        action: {
          label: 'Rückgängig',
          onClick: async () => {
            try {
              await mailApi.restoreMessage(accountId, messageId, result.previousFolderId);
              queryClient.invalidateQueries({ queryKey: ['messages'] });
              toast.success('E-Mail wiederhergestellt');
            } catch (error) {
              toast.error('Fehler beim Wiederherstellen');
            }
          },
        },
        duration: 8000, // 8 seconds to undo
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Löschen');
      console.error('Delete failed:', error);
    },
  });
  
  // Update column mutation (for smart columns)
  const updateColumnMutation = useMutation({
    mutationFn: async (data: SmartColumnData) => {
      if (!data.id) throw new Error('Column ID required');
      return boardsApi.updateColumn(data.id, {
        displayName: data.displayName,
        sourceId: data.sourceId,
        mailAccountId: data.mailAccountId,
        filterConfig: data.filterConfig,
        icon: data.icon,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      queryClient.invalidateQueries({ queryKey: ['smartMessages'] });
      toast.success('Spalte aktualisiert');
      setEditSmartColumn(null);
    },
    onError: (error) => {
      toast.error('Fehler beim Speichern');
      console.error('Update column failed:', error);
    },
  });
  
  // Delete column mutation
  const deleteColumnMutation = useMutation({
    mutationFn: async (columnId: string) => {
      return boardsApi.deleteColumn(columnId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board'] });
      toast.success('Spalte gelöscht');
      setEditSmartColumn(null);
    },
    onError: (error) => {
      toast.error('Fehler beim Löschen');
      console.error('Delete column failed:', error);
    },
  });
  
  // Update AI column mutation
  const updateAiColumnMutation = useMutation({
    mutationFn: async ({ columnId, input }: { columnId: string; input: UpdateSmartColumnInput }) => {
      return smartColumnsApi.update(columnId, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      queryClient.invalidateQueries({ queryKey: ['aiMatches'] });
      toast.success('AI-Spalte aktualisiert');
      setEditAiColumn(null);
    },
    onError: (error) => {
      toast.error('Fehler beim Speichern');
      console.error('Update AI column failed:', error);
    },
  });
  
  // Delete AI column mutation
  const deleteAiColumnMutation = useMutation({
    mutationFn: async (columnId: string) => {
      return smartColumnsApi.delete(columnId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      queryClient.invalidateQueries({ queryKey: ['aiMatches'] });
      toast.success('AI-Spalte gelöscht');
      setEditAiColumn(null);
    },
    onError: (error) => {
      toast.error('Fehler beim Löschen');
      console.error('Delete AI column failed:', error);
    },
  });
  
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'mail') {
      setActiveMessage(active.data.current.message);
    }
  };
  
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveMessage(null);
    
    if (!over) return;
    
    const activeData = active.data.current;
    const overData = over.data.current;
    
    // Only handle mail drops onto columns
    if (activeData?.type !== 'mail' || overData?.type !== 'column') return;
    
    // Don't move to same column
    if (activeData.columnId === overData.column.id) return;
    
    const targetColumn = overData.column;
    
    if (targetColumn.sourceType === 'folder') {
      // Move to folder
      moveMutation.mutate({
        accountId: activeData.accountId,
        messageId: activeData.message.id,
        targetFolderId: targetColumn.sourceId,
      });
    } else if (targetColumn.sourceType === 'category') {
      // Set category on message (extract category name from sourceId "category:Name")
      const categoryName = targetColumn.sourceId.replace(/^category:/, '');
      categoryMutation.mutate({
        accountId: activeData.accountId,
        messageId: activeData.message.id,
        categoryName,
      });
    } else if (overData?.type === 'meeting-prep-column') {
      // Meeting Prep column: Email linking is handled via the event cards
      // For now, show a toast that the user should drop on a specific event
      toast.info('Ziehe die Mail auf einen spezifischen Termin um sie zu verknüpfen');
    } else {
      toast.error('Dieser Spaltentyp unterstützt kein Drag & Drop');
    }
  };
  
  // === LIST MODE SIDEBAR STATE ===
  const [activeListColumn, setActiveListColumn] = useState<string | null>(null);
  
  // All renderable columns (regular + AI) for sidebar
  const allRenderableColumns = useMemo(() => {
    const regular = columns.map((col: any) => ({
      id: col.id,
      displayName: col.displayName,
      icon: col.icon,
      sourceType: col.sourceType,
      isAi: false,
      raw: col,
    }));
    const ai = enabledAiColumns.map((col) => ({
      id: `ai-${col.id}`,
      displayName: col.name,
      icon: col.icon,
      sourceType: 'ai' as string,
      isAi: true,
      raw: col,
    }));
    return [...regular, ...ai];
  }, [columns, enabledAiColumns]);
  
  // Default to first column when entering list mode or when active column disappears
  useEffect(() => {
    if (boardViewMode !== 'list') return;
    if (allRenderableColumns.length === 0) {
      setActiveListColumn(null);
      return;
    }
    const exists = allRenderableColumns.some(c => c.id === activeListColumn);
    if (!activeListColumn || !exists) {
      setActiveListColumn(allRenderableColumns[0].id);
    }
  }, [boardViewMode, allRenderableColumns, activeListColumn]);
  
  // Group columns by sourceType for sidebar sections
  const sidebarSections = useMemo(() => {
    const groups: Record<string, typeof allRenderableColumns> = {};
    const order = ['folder', 'category', 'smart', 'meeting-prep', 'ai'];
    for (const col of allRenderableColumns) {
      const key = col.sourceType;
      if (!groups[key]) groups[key] = [];
      groups[key].push(col);
    }
    const sectionLabels: Record<string, string> = {
      folder: '📁 Ordner',
      category: '🏷️ Kategorien',
      smart: '🔍 Smart',
      'meeting-prep': '📅 Termine',
      ai: '🤖 AI',
    };
    return order
      .filter(k => groups[k]?.length)
      .map(k => ({ key: k, label: sectionLabels[k] || k, columns: groups[k] }));
  }, [allRenderableColumns]);
  
  // Check if first column is frozen
  const firstColumnFrozen = columns.length > 0 && columns[0].frozen;
  
  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div id="board-split-container" className="flex-1 flex h-full" style={{ overflow: 'hidden', minHeight: 0 }}>
        {/* Columns + Toggle wrapper */}
        <div 
          className="flex flex-col"
          style={{ 
            ...(selectedMail && !usePopup && boardViewMode === 'list'
              ? { width: `${dividerPos}%`, flex: 'none' }
              : { 
                  flex: selectedMail && !usePopup 
                    ? '0 0 60%'
                    : '1 1 100%',
                }),
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            height: '100%',
          }}
        >
          {/* View Toggle */}
          <div className="flex items-center justify-end px-4 pt-2 pb-0 gap-1 flex-shrink-0">
            <button
              onClick={() => setBoardViewMode('board')}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                boardViewMode === 'board' ? 'bg-purple-500/20' : ''
              )}
              style={{ color: boardViewMode === 'board' ? 'var(--brand-primary)' : 'var(--text-tertiary)' }}
              title="Board-Ansicht"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setBoardViewMode('list')}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                boardViewMode === 'list' ? 'bg-purple-500/20' : ''
              )}
              style={{ color: boardViewMode === 'list' ? 'var(--brand-primary)' : 'var(--text-tertiary)' }}
              title="Listen-Ansicht"
            >
              <LayoutList className="w-4 h-4" />
            </button>
          </div>
          {/* Columns Container */}
          {boardViewMode === 'list' ? (
            /* === LIST MODE: Sidebar + Content === */
            <div className="flex-1 flex" style={{ overflow: 'hidden', minHeight: 0, height: '100%' }}>
              {/* Sidebar - hidden on mobile, shown as collapsible or tabs */}
              <div 
                className={cn(
                  "flex-shrink-0 overflow-y-auto transition-all",
                  isMobile ? "hidden" : "w-56"
                )}
                style={{ borderRight: isMobile ? 'none' : '1px solid var(--border-default)' }}
              >
                {sidebarSections.map((section) => (
                  <div key={section.key}>
                    <div 
                      className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      {section.label}
                    </div>
                    {section.columns.map((col) => {
                      const count = columnMessageIds[col.id]?.length ?? 
                        (col.isAi ? (aiMatches[(col.raw as SmartAiColumn).id] || []).length : 0);
                      return (
                        <button
                          key={col.id}
                          onClick={() => setActiveListColumn(col.id)}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                            activeListColumn === col.id && 'font-medium'
                          )}
                          style={{
                            backgroundColor: activeListColumn === col.id ? 'var(--brand-primary-alpha, rgba(139,92,246,0.1))' : 'transparent',
                            color: activeListColumn === col.id ? 'var(--brand-primary)' : 'var(--text-primary)',
                          }}
                        >
                          <span>{col.icon || '📁'}</span>
                          <span className="truncate flex-1">{col.displayName}</span>
                          {count > 0 && (
                            <span className="text-xs opacity-60">{count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
              
              {/* Content: Active Column */}
              <div style={{ flex: '1 1 0%', minHeight: 0, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Mobile Column Selector */}
                {isMobile && allRenderableColumns.length > 0 && (
                  <div className="flex-shrink-0 p-2 border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <select
                      value={activeListColumn || ''}
                      onChange={(e) => setActiveListColumn(e.target.value)}
                      className="w-full p-2 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-default)'
                      }}
                    >
                      {sidebarSections.map((section) => (
                        <optgroup key={section.key} label={section.label}>
                          {section.columns.map((col) => {
                            const count = columnMessageIds[col.id]?.length ?? 
                              (col.isAi ? (aiMatches[(col.raw as SmartAiColumn).id] || []).length : 0);
                            return (
                              <option key={col.id} value={col.id}>
                                {col.icon} {col.displayName} {count > 0 ? `(${count})` : ''}
                              </option>
                            );
                          })}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}
                {allRenderableColumns.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-text-muted mb-2">Keine Spalten vorhanden</p>
                      <p className="text-sm text-text-disabled">
                        Bearbeite das Board, um Spalten hinzuzufügen
                      </p>
                    </div>
                  </div>
                ) : (() => {
                  const activeCol = allRenderableColumns.find(c => c.id === activeListColumn);
                  if (!activeCol) return null;
                  
                  if (activeCol.isAi) {
                    const aiColumn = activeCol.raw as SmartAiColumn;
                    const columnMatches = aiMatches[aiColumn.id] || [];
                    const columnMatchDetails = aiMatchesDetailed[aiColumn.id] || [];
                    return (
                      <AiKanbanColumn
                        key={`ai-${aiColumn.id}`}
                        column={aiColumn}
                        account={primaryAccount}
                        aiMatches={columnMatches}
                        aiMatchDetails={columnMatchDetails}
                        onSelectMail={(messageId: string) => {
                          if (primaryAccount) {
                            setSelectedMail({
                              accountId: primaryAccount.id,
                              messageId,
                              provider: primaryAccount.provider,
                            });
                          }
                        }}
                        selectedMessageId={selectedMail?.messageId}
                        isDragEnabled={true}
                        onQuickAction={handleQuickAction}
                        onAddTodo={handleAddTodo}
                        onMessagesLoaded={(ids) => handleMessagesLoaded(`ai-${aiColumn.id}`, ids)}
                        isScanning={isAiScanning}
                        onEditColumn={() => setEditAiColumn(aiColumn)}
                        viewMode={boardViewMode}
                        showSummary={summaryModes[`ai-${aiColumn.id}`] || false}
                        onToggleSummary={() => toggleSummaryMode(`ai-${aiColumn.id}`)}
                      />
                    );
                  }
                  
                  const column = activeCol.raw;
                  const index = columns.indexOf(column);
                  const account = getAccount(column.mailAccountId);
                  const commonProps = {
                    key: column.id,
                    column,
                    account,
                    onSelectMail: (messageId: string) => {
                      if (account) {
                        setSelectedMail({
                          accountId: account.id,
                          messageId,
                          provider: account.provider,
                        });
                      }
                    },
                    selectedMessageId: selectedMail?.messageId,
                    isFrozen: index === 0 && column.frozen,
                    isDragEnabled: true,
                    onQuickAction: handleQuickAction,
                    onAddTodo: handleAddTodo,
                    excludeMessageIds: getExcludedIds(index),
                    onMessagesLoaded: (ids: string[]) => handleMessagesLoaded(column.id, ids),
                    showCompletionPopup: board.showCompletionPopup ?? true,
                    viewMode: boardViewMode,
                  };
                  
                  if (column.sourceType === 'smart') {
                    return (
                      <SmartKanbanColumn
                        {...commonProps}
                        onEditColumn={() => {
                          setEditSmartColumn({
                            id: column.id,
                            displayName: column.displayName,
                            sourceId: column.sourceId,
                            mailAccountId: column.mailAccountId,
                            filterConfig: column.filterConfig,
                            icon: column.icon,
                          });
                        }}
                      />
                    );
                  }
                  
                  if (column.sourceType === 'meeting-prep') {
                    return (
                      <MeetingPrepColumn
                        key={column.id}
                        column={column}
                        account={account}
                        onSelectMail={(messageId: string) => {
                          if (account) {
                            setSelectedMail({
                              accountId: account.id,
                              messageId,
                              provider: account.provider,
                            });
                          }
                        }}
                        selectedMessageId={selectedMail?.messageId}
                        isFrozen={index === 0 && column.frozen}
                        isDragEnabled={true}
                        onQuickAction={handleQuickAction}
                        onAddTodo={handleAddTodo}
                        onMessagesLoaded={(ids) => handleMessagesLoaded(column.id, ids)}
                      />
                    );
                  }
                  
                  return (
                    <KanbanColumn
                      {...commonProps}
                      showOnlyWithTasks={showOnlyWithTasks}
                      hiddenCategoryNames={hiddenCategoryNames}
                      showSummary={summaryModes[column.id] || false}
                      onToggleSummary={() => toggleSummaryMode(column.id)}
                    />
                  );
                })()}
              </div>
            </div>
          ) : (
            /* === BOARD MODE: Horizontal scroll === */
            <div 
              className={cn(
                'p-4 flex-1',
                'flex gap-4 overflow-x-auto'
              )}
            >
          {columns.length === 0 && enabledAiColumns.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-text-muted mb-2">Keine Spalten vorhanden</p>
                <p className="text-sm text-text-disabled">
                  Bearbeite das Board, um Spalten hinzuzufügen
                </p>
              </div>
            </div>
          ) : (
            columns.map((column: any, index: number) => {
              const account = getAccount(column.mailAccountId);
              const commonProps = {
                key: column.id,
                column,
                account,
                onSelectMail: (messageId: string) => {
                  if (account) {
                    setSelectedMail({
                      accountId: account.id,
                      messageId,
                      provider: account.provider,
                    });
                  }
                },
                selectedMessageId: selectedMail?.messageId,
                isFrozen: index === 0 && column.frozen,
                isDragEnabled: true,
                onQuickAction: handleQuickAction,
                onAddTodo: handleAddTodo,
                excludeMessageIds: getExcludedIds(index),
                onMessagesLoaded: (ids: string[]) => handleMessagesLoaded(column.id, ids),
                showCompletionPopup: board.showCompletionPopup ?? true,
                viewMode: boardViewMode,
              };
              
              // Use SmartKanbanColumn for smart columns
              if (column.sourceType === 'smart') {
                return (
                  <SmartKanbanColumn
                    {...commonProps}
                    onEditColumn={() => {
                      setEditSmartColumn({
                        id: column.id,
                        displayName: column.displayName,
                        sourceId: column.sourceId,
                        mailAccountId: column.mailAccountId,
                        filterConfig: column.filterConfig,
                        icon: column.icon,
                      });
                    }}
                  />
                );
              }
              
              // Use MeetingPrepColumn for meeting-prep columns
              if (column.sourceType === 'meeting-prep') {
                return (
                  <MeetingPrepColumn
                    key={column.id}
                    column={column}
                    account={account}
                    onSelectMail={(messageId: string) => {
                      if (account) {
                        setSelectedMail({
                          accountId: account.id,
                          messageId,
                          provider: account.provider,
                        });
                      }
                    }}
                    selectedMessageId={selectedMail?.messageId}
                    isFrozen={index === 0 && column.frozen}
                    isDragEnabled={true}
                    onQuickAction={handleQuickAction}
                    onAddTodo={handleAddTodo}
                    onMessagesLoaded={(ids) => handleMessagesLoaded(column.id, ids)}
                  />
                );
              }
              
              // Regular column
              return (
                <KanbanColumn
                  {...commonProps}
                  showOnlyWithTasks={showOnlyWithTasks}
                  hiddenCategoryNames={hiddenCategoryNames}
                  showSummary={summaryModes[column.id] || false}
                  onToggleSummary={() => toggleSummaryMode(column.id)}
                />
              );
            })
          )}
          
          {/* AI Columns - rendered after regular columns */}
          {enabledAiColumns.map((aiColumn) => {
            const columnMatches = aiMatches[aiColumn.id] || [];
            const columnMatchDetails = aiMatchesDetailed[aiColumn.id] || [];
            
            return (
              <AiKanbanColumn
                key={`ai-${aiColumn.id}`}
                column={aiColumn}
                account={primaryAccount}
                aiMatches={columnMatches}
                aiMatchDetails={columnMatchDetails}
                onSelectMail={(messageId: string) => {
                  if (primaryAccount) {
                    setSelectedMail({
                      accountId: primaryAccount.id,
                      messageId,
                      provider: primaryAccount.provider,
                    });
                  }
                }}
                selectedMessageId={selectedMail?.messageId}
                isDragEnabled={true}
                onQuickAction={handleQuickAction}
                onAddTodo={handleAddTodo}
                onMessagesLoaded={(ids) => handleMessagesLoaded(`ai-${aiColumn.id}`, ids)}
                isScanning={isAiScanning}
                onEditColumn={() => setEditAiColumn(aiColumn)}
                viewMode={boardViewMode}
                showSummary={summaryModes[`ai-${aiColumn.id}`] || false}
                onToggleSummary={() => toggleSummaryMode(`ai-${aiColumn.id}`)}
              />
            );
          })}
          
          </div>
          )}
        </div>
        
        {/* Resizable Divider (list-mode only) */}
        {selectedMail && !usePopup && boardViewMode === 'list' && (
          <div 
            onMouseDown={() => { isDraggingDivider.current = true; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; }}
            className="w-1 hover:w-1.5 cursor-col-resize hover:bg-purple-500/30 transition-all flex-shrink-0 hidden md:block"
            style={{ backgroundColor: 'var(--border-default)' }}
          />
        )}
        
        {/* Reading Pane - Sidebar mode (Desktop with sidebar setting) */}
        {selectedMail && !usePopup && (
          <div 
            className="border-l border-border-default flex-shrink-0 hidden md:block"
            style={{ 
              ...(boardViewMode === 'list' 
                ? { width: `${100 - dividerPos}%`, minWidth: '300px' }
                : { width: '40%', minWidth: '400px', maxWidth: '600px' }),
              height: '100%',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <ReadingPane
              accountId={selectedMail.accountId}
              messageId={selectedMail.messageId}
              provider={selectedMail.provider}
              onClose={() => setSelectedMail(null)}
              onQuickAction={(data) => setQuickActionsMail({ ...data, columnId: undefined })}
            />
          </div>
        )}
      </div>
      
      {/* Reading Pane - Popup mode (Mobile or Desktop with popup setting) */}
      {selectedMail && usePopup && (
        <MailModal
          accountId={selectedMail.accountId}
          messageId={selectedMail.messageId}
          provider={selectedMail.provider}
          onClose={() => setSelectedMail(null)}
          onQuickAction={(data) => setQuickActionsMail({ ...data, columnId: undefined })}
        />
      )}
      
      {/* Drag Overlay - shows dragged item */}
      <DragOverlay dropAnimation={null}>
        {activeMessage ? (
          <DraggedMailCard message={activeMessage} />
        ) : null}
      </DragOverlay>
      
      {/* Mobile Quick Actions Modal - use allColumns to include hidden categories */}
      {quickActionsMail && (
        <MobileQuickActionsModal
          mail={quickActionsMail}
          columns={allColumns}
          provider={getAccount(quickActionsMail.accountId)?.provider || 'microsoft'}
          onMoveToColumn={handleMoveToColumn}
          onMoveToFolder={(folderId, folderName) => {
            moveMutation.mutate({
              accountId: quickActionsMail.accountId,
              messageId: quickActionsMail.message.id,
              targetFolderId: folderId,
            });
            setQuickActionsMail(null);
          }}
          onRemoveCategory={(categoryName) => {
            removeCategoryMutation.mutate({
              accountId: quickActionsMail.accountId,
              messageId: quickActionsMail.message.id,
              categoryName: categoryName || undefined,
            });
            setQuickActionsMail(null);
          }}
          onDelete={() => {
            deleteMutation.mutate({
              accountId: quickActionsMail.accountId,
              messageId: quickActionsMail.message.id,
            });
            setQuickActionsMail(null);
          }}
          onClose={() => setQuickActionsMail(null)}
        />
      )}
      
      {/* Smart Column Edit Modal */}
      <SmartColumnModal
        isOpen={!!editSmartColumn}
        onClose={() => setEditSmartColumn(null)}
        onSave={(data) => updateColumnMutation.mutate(data)}
        onDelete={(columnId) => deleteColumnMutation.mutate(columnId)}
        accounts={accounts}
        folders={allColumns.filter((c: any) => c.sourceType === 'folder')}
        editColumn={editSmartColumn || undefined}
      />
      
      {/* AI Column Edit Modal */}
      {editAiColumn && (
        <SmartColumnCreator
          boardId={board.id}
          folders={foldersForAi.map((f: any) => ({
            id: f.id,
            name: f.displayName || f.name,
            type: (f.type || 'folder') as 'folder' | 'category' | 'label',
          }))}
          mailAccountId={primaryAccount?.id}
          onClose={() => setEditAiColumn(null)}
          onCreate={async () => {}} // Not used in edit mode
          editColumn={{
            ...editAiColumn,
            systemPrompt: editAiColumn.systemPrompt ?? null,
            providerId: editAiColumn.providerId ?? null,
            color: editAiColumn.color ?? null,
            icon: editAiColumn.icon ?? null,
          } as any}
          onUpdate={async (columnId, input) => {
            await updateAiColumnMutation.mutateAsync({ columnId, input });
          }}
          onDelete={async (columnId) => {
            await deleteAiColumnMutation.mutateAsync(columnId);
          }}
        />
      )}
      
      {/* Add to Todo Modal - triggered from card quick action */}
      {addTodoMail && (
        <AddToTodoModal
          isOpen={true}
          onClose={() => {
            setAddTodoMail(null);
            queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
          }}
          accountId={addTodoMail.accountId}
          messageId={addTodoMail.message.id}
          mailSubject={addTodoMail.message.subject}
          mailFrom={addTodoMail.message.from?.name || addTodoMail.message.from?.email || 'Unbekannt'}
          provider={addTodoMail.provider as 'microsoft' | 'google'}
        />
      )}
      
    </DndContext>
  );
}

// Mobile Quick Actions Modal
function MobileQuickActionsModal({ 
  mail, 
  columns, 
  provider,
  onMoveToColumn,
  onMoveToFolder,
  onRemoveCategory,
  onDelete,
  onClose 
}: { 
  mail: { message: any; accountId: string; columnId?: string };
  columns: any[];
  provider: 'microsoft' | 'google';
  onMoveToColumn: (column: any) => void;
  onMoveToFolder: (folderId: string, folderName: string) => void;
  onRemoveCategory: (categoryName: string | null) => void; // null = remove all
  onDelete: () => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  
  // Load all folders for this account
  const { data: foldersData, isLoading: foldersLoading } = useQuery({
    queryKey: ['folders', mail.accountId],
    queryFn: () => mailApi.getFolders(mail.accountId),
    staleTime: 5 * 60 * 1000, // 5 min cache
  });
  
  const allFolders = (foldersData?.folders || []) as Array<{ id: string; name: string; type?: string }>;
  
  // Filter folders by search (recursive flattening)
  const flattenFolders = (folders: any[], prefix = ''): Array<{ id: string; name: string; displayName: string }> => {
    const result: Array<{ id: string; name: string; displayName: string }> = [];
    for (const f of folders) {
      const displayName = prefix ? `${prefix} / ${f.name}` : f.name;
      result.push({ id: f.id, name: f.name, displayName });
      if (f.children?.length) {
        result.push(...flattenFolders(f.children, displayName));
      }
    }
    return result;
  };
  
  const flatFolders = useMemo(() => flattenFolders(allFolders), [allFolders]);
  const filteredFolders = folderSearch 
    ? flatFolders.filter(f => f.displayName.toLowerCase().includes(folderSearch.toLowerCase()))
    : flatFolders;
  
  // Filter out current column and separate folders from categories
  const availableColumns = columns.filter(c => c.id !== mail.columnId);
  const folderColumns = availableColumns.filter(c => c.sourceType === 'folder');
  const categoryColumns = availableColumns.filter(c => c.sourceType === 'category');
  
  // Current categories on the mail
  const currentCategories: string[] = mail.message.categories || [];
  
  // Check if mail has a todo link
  const { data: todoLinkData } = useQuery({
    queryKey: ['mailTodoLink', mail.accountId, mail.message.id],
    queryFn: () => todoApi.getMailLink(mail.accountId, mail.message.id),
    staleTime: 30 * 1000,
  });
  
  const todoLink = todoLinkData?.link;
  
  // Toggle todo completion
  const toggleMutation = useMutation({
    mutationFn: () => todoApi.toggleMailTodo(mail.accountId, mail.message.id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mailTodoLink', mail.accountId, mail.message.id] });
      queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
      toast.success(data.message);
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren der Aufgabe');
    },
  });
  
  const handleOpenInOriginal = async () => {
    const { url } = await mailApi.getOpenUrl(mail.accountId, mail.message.id);
    window.open(url, '_blank');
    onClose();
  };
  
  const handleReply = async () => {
    const { url } = await mailApi.getReplyUrl(mail.accountId, mail.message.id);
    window.open(url, '_blank');
    onClose();
  };
  
  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50"
        onClick={onClose}
      >
        <div 
          className="w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom duration-300"
          style={{ backgroundColor: 'var(--bg-primary)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between px-4 py-3"
            style={{ 
              borderBottom: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-secondary)',
            }}
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Aktionen</p>
              <p 
                className="font-semibold truncate"
                style={{ color: 'var(--text-primary)' }}
              >
                {mail.message.subject}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 rounded-lg"
              style={{ color: 'var(--text-tertiary)' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 max-h-[60vh] overflow-y-auto">
            {/* Mail Actions Section */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                <span className="text-xs font-medium uppercase" style={{ color: 'var(--text-tertiary)' }}>
                  E-Mail Aktionen
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleOpenInOriginal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg hover:opacity-90 transition-opacity font-medium"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                  Im Original öffnen
                </button>
                <button
                  onClick={handleReply}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors font-medium"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <Reply className="w-4 h-4" />
                  Antworten
                </button>
                {todoLink ? (
                  <button
                    onClick={() => toggleMutation.mutate()}
                    disabled={toggleMutation.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors font-medium"
                    style={{
                      backgroundColor: todoLink.completed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                      border: `1px solid ${todoLink.completed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`,
                      color: todoLink.completed ? 'var(--color-success)' : 'var(--brand-primary)',
                    }}
                    title={todoLink.completed ? 'Als unerledigt markieren' : 'Als erledigt markieren'}
                  >
                    {toggleMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : todoLink.completed ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    {todoLink.completed ? 'Erledigt' : 'Aufgabe'}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowTodoModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors font-medium"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                    title={`Zu ${provider === 'microsoft' ? 'Microsoft To-Do' : 'Google Tasks'} hinzufügen`}
                  >
                    <CheckSquare className="w-4 h-4" />
                    Zu To-Do
                  </button>
                )}
              </div>
            </div>
            
            {/* Folders Section */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FolderInput className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                <span className="text-xs font-medium uppercase" style={{ color: 'var(--text-tertiary)' }}>
                  In Ordner verschieben
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {folderColumns.map(column => (
                  <button
                    key={column.id}
                    onClick={() => onMoveToColumn(column)}
                    className="flex items-center gap-2 p-3 rounded-lg text-left transition-colors"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-default)',
                    }}
                    onTouchStart={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                    onTouchEnd={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                  >
                    <span className="text-lg">{column.icon || '📂'}</span>
                    <span 
                      className="text-sm font-medium truncate"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {column.displayName}
                    </span>
                  </button>
                ))}
                
                {/* Browse All Folders Button */}
                <button
                  onClick={() => setShowFolderBrowser(true)}
                  className="flex items-center gap-2 p-3 rounded-lg text-left transition-colors col-span-2"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    border: '1px dashed var(--border-default)',
                  }}
                  onTouchStart={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                  onTouchEnd={e => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                >
                  <MoreHorizontal className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                  <span 
                    className="text-sm font-medium"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Weitere Ordner...
                  </span>
                </button>
              </div>
            </div>
            
            {/* Folder Browser Modal */}
            {showFolderBrowser && (
              <div 
                className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
                onClick={() => setShowFolderBrowser(false)}
              >
                <div 
                  className="w-full max-w-md rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200"
                  style={{ backgroundColor: 'var(--bg-primary)' }}
                  onClick={e => e.stopPropagation()}
                >
                  <div 
                    className="flex items-center justify-between px-4 py-3"
                    style={{ 
                      borderBottom: '1px solid var(--border-default)',
                      backgroundColor: 'var(--bg-secondary)',
                    }}
                  >
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Ordner auswählen
                    </span>
                    <button 
                      onClick={() => setShowFolderBrowser(false)}
                      className="p-2 rounded-lg"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Search */}
                  <div className="p-3" style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <div 
                      className="flex items-center gap-2 px-3 py-2 rounded-lg"
                      style={{ backgroundColor: 'var(--bg-secondary)' }}
                    >
                      <Search className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                      <input
                        type="text"
                        value={folderSearch}
                        onChange={e => setFolderSearch(e.target.value)}
                        placeholder="Ordner suchen..."
                        className="flex-1 bg-transparent border-none outline-none text-sm"
                        style={{ color: 'var(--text-primary)' }}
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  {/* Folder List */}
                  <div className="max-h-[50vh] overflow-y-auto p-2">
                    {foldersLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                      </div>
                    ) : filteredFolders.length === 0 ? (
                      <div className="text-center py-8 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        Keine Ordner gefunden
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredFolders.map(folder => (
                          <button
                            key={folder.id}
                            onClick={() => {
                              onMoveToFolder(folder.id, folder.name);
                              setShowFolderBrowser(false);
                              onClose();
                            }}
                            className="flex items-center gap-3 w-full p-3 rounded-lg text-left transition-colors"
                            style={{ backgroundColor: 'transparent' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Folder className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                            <span 
                              className="text-sm truncate"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {folder.displayName}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Categories Section */}
            {categoryColumns.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                  <span className="text-xs font-medium uppercase" style={{ color: 'var(--text-tertiary)' }}>
                    Kategorie setzen
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {categoryColumns.map(column => (
                    <button
                      key={column.id}
                      onClick={() => onMoveToColumn(column)}
                      className="flex items-center gap-2 p-3 rounded-lg text-left transition-colors"
                      style={{
                        backgroundColor: 'var(--bg-secondary)',
                        border: column.hidden 
                          ? '1px dashed var(--border-default)' 
                          : '1px solid var(--border-default)',
                        opacity: column.hidden ? 0.8 : 1,
                      }}
                      onTouchStart={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                      onTouchEnd={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    >
                      <span className="text-lg">{column.hidden ? '👻' : (column.icon || '🏷️')}</span>
                      <span 
                        className="text-sm font-medium truncate"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {column.displayName}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Remove Categories Section - only show if message has categories */}
            {currentCategories.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <X className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                  <span className="text-xs font-medium uppercase" style={{ color: 'var(--text-tertiary)' }}>
                    Kategorie(n) entfernen
                  </span>
                </div>
                <div className="space-y-2">
                  {/* Remove all button */}
                  {currentCategories.length > 1 && (
                    <button
                      onClick={() => onRemoveCategory(null)}
                      className="flex items-center gap-2 w-full p-3 rounded-lg text-left transition-colors"
                      style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      }}
                      onTouchStart={e => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'}
                      onTouchEnd={e => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)'}
                    >
                      <span className="text-lg">🗑️</span>
                      <span 
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-error)' }}
                      >
                        Alle Kategorien entfernen ({currentCategories.length})
                      </span>
                    </button>
                  )}
                  
                  {/* Individual categories */}
                  <div className="grid grid-cols-2 gap-2">
                    {currentCategories.map((category: string) => (
                      <button
                        key={category}
                        onClick={() => onRemoveCategory(category)}
                        className="flex items-center gap-2 p-3 rounded-lg text-left transition-colors"
                        style={{
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-default)',
                        }}
                        onTouchStart={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                        onTouchEnd={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                      >
                        <X className="w-4 h-4" style={{ color: 'var(--color-error)' }} />
                        <span 
                          className="text-sm font-medium truncate"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {category}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Danger Zone - Delete */}
          <div className="px-4 pb-2">
            <button
              onClick={onDelete}
              className="flex items-center justify-center gap-2 w-full p-3 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: 'var(--color-error)',
              }}
              onTouchStart={e => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)'}
              onTouchEnd={e => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
            >
              <Trash2 className="w-4 h-4" />
              Löschen
            </button>
          </div>
          
          {/* Cancel button for mobile */}
          <div 
            className="p-4 sm:hidden"
            style={{ borderTop: '1px solid var(--border-default)' }}
          >
            <button
              onClick={onClose}
              className="w-full py-3 rounded-lg font-medium"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
      
      {/* Add to To-Do Modal */}
      <AddToTodoModal
        isOpen={showTodoModal}
        onClose={() => {
          setShowTodoModal(false);
          queryClient.invalidateQueries({ queryKey: ['mailTodoLink', mail.accountId, mail.message.id] });
          queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
        }}
        accountId={mail.accountId}
        messageId={mail.message.id}
        mailSubject={mail.message.subject}
        mailFrom={mail.message.from?.name || mail.message.from?.email || 'Unbekannt'}
        provider={provider}
      />
    </>
  );
}

// Simplified mail card for drag overlay
function DraggedMailCard({ message }: { message: any }) {
  return (
    <div
      className="w-72 p-3 rounded-lg shadow-2xl"
      style={{
        backgroundColor: 'var(--bg-primary)',
        border: '2px solid var(--brand-primary)',
        transform: 'rotate(3deg)',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span 
          className={cn(
            'text-sm truncate font-semibold'
          )}
          style={{ color: 'var(--text-primary)' }}
        >
          {message.from.name || message.from.email}
        </span>
        <span 
          className="text-xs whitespace-nowrap"
          style={{ color: 'var(--text-tertiary)' }}
        >
          {formatDate(message.receivedAt)}
        </span>
      </div>
      
      <p 
        className="text-sm truncate font-semibold"
        style={{ color: 'var(--text-primary)' }}
      >
        {message.subject}
      </p>
      
      <div className="flex items-center gap-2 mt-2">
        {message.hasAttachments && (
          <Paperclip className="w-3 h-3" style={{ color: 'var(--text-tertiary)' }} />
        )}
        {message.importance === 'high' && (
          <Star className="w-3 h-3 text-warning fill-warning" />
        )}
      </div>
    </div>
  );
}
